<script setup lang="ts">
import { ref, watch } from 'vue';
import { tutorialImage, youtubeVideoId, type TutorialStep } from '../offer-tutorial';
const props = defineProps<{
  steps: TutorialStep[];
  editable?: boolean;
  tutorialId?: string;
  title?: string;
}>();
const emit = defineEmits<{ updated: [] }>();
const draft = ref<TutorialStep[]>([]);
const busy = ref(false);
const error = ref('');
const saved = ref(false);
const playing = ref<number[]>([]);
function selectMedia(index: number, video: boolean): void {
  const step = draft.value[index];
  draft.value[index] = video
    ? { caption: step.caption, youtubeUrl: '' }
    : { caption: step.caption, imageDataUrl: '' };
  saved.value = false;
}

const draftTitle = ref(props.title ?? 'Réglage physique des Boxes');
watch(
  () => props.title,
  (value) => {
    draftTitle.value = value ?? 'Réglage physique des Boxes';
  },
);
watch(
  () => props.steps,
  (steps) => {
    playing.value = [];
    draft.value = steps.map((s) => ({ ...s }));
  },
  { immediate: true },
);
async function chooseImage(index: number, event: { target: unknown }): Promise<void> {
  const input = event.target as { files: { 0?: globalThis.Blob } | null; value: string };
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  busy.value = true;
  error.value = '';
  saved.value = false;
  try {
    draft.value[index].imageDataUrl = await tutorialImage(file);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Image illisible.';
  } finally {
    busy.value = false;
  }
}
function addStep(): void {
  if (draft.value.length < 6) {
    draft.value.push({ imageDataUrl: '', caption: '' });
    saved.value = false;
  }
}
function move(index: number, delta: number): void {
  const target = index + delta;
  if (target < 0 || target >= draft.value.length) return;
  [draft.value[index], draft.value[target]] = [draft.value[target], draft.value[index]];
  saved.value = false;
}
async function save(): Promise<void> {
  if (busy.value) return;
  if (
    draft.value.some(
      (s) =>
        !s.caption.trim() ||
        ('youtubeUrl' in s ? !youtubeVideoId(s.youtubeUrl) : !s.imageDataUrl),
    )
  ) {
    error.value = 'Ajoutez une image ou un lien YouTube valide et une légende à chaque étape.';
    return;
  }
  busy.value = true;
  error.value = '';
  saved.value = false;
  try {
    const response = await globalThis.fetch(
      props.tutorialId
        ? '/admin/api/team-tutorials/' + encodeURIComponent(props.tutorialId)
        : '/admin/api/physical-box-tutorial',
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(props.tutorialId ? { title: draftTitle.value } : {}),
          steps: draft.value,
        }),
      },
    );
    if (!response.ok)
      throw new Error(
        'Enregistrement impossible. Vérifiez les images, les liens YouTube et les légendes.',
      );
    saved.value = true;
    emit('updated');
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Enregistrement impossible.';
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <div class="tutorial-content">
    <p v-if="!editable && !steps.length">
      Le guide sera disponible après sa création par l’administrateur.
    </p>
    <template v-if="!editable">
      <figure v-for="(step, index) in steps" :key="index">
        <img
          v-if="step.imageDataUrl"
          :src="step.imageDataUrl"
          :alt="`Étape ${index + 1} : ${step.caption}`"
          loading="lazy"
        />
        <div v-if="step.youtubeUrl && youtubeVideoId(step.youtubeUrl)" class="tutorial-video">
          <iframe
            v-if="playing.includes(index)"
            :src="'https://www.youtube-nocookie.com/embed/' + youtubeVideoId(step.youtubeUrl)"
            :title="`Vidéo étape ${index + 1} : ${step.caption}`"
            allowfullscreen
            referrerpolicy="strict-origin-when-cross-origin"
          />
          <button
            v-else
            type="button"
            class="button button-secondary"
            @click="playing.push(index)"
          >
            Lire la vidéo YouTube
          </button>
          <a
            :href="'https://www.youtube.com/watch?v=' + youtubeVideoId(step.youtubeUrl)"
            target="_blank"
            rel="noopener noreferrer"
            >Ouvrir dans YouTube</a
          >
        </div>
        <figcaption>
          <strong>{{ index + 1 }}.</strong> {{ step.caption }}
        </figcaption>
      </figure>
    </template>
    <fieldset v-else :disabled="busy">
      <legend class="sr-only">Modifier le tutoriel</legend>
      <label v-if="tutorialId" :for="'tutorial-title-' + tutorialId">Nom de la rubrique</label>
      <input
        v-if="tutorialId"
        :id="'tutorial-title-' + tutorialId"
        v-model="draftTitle"
        maxlength="120"
        required
        @input="saved = false"
      />
      <p>
        Jusqu’à 6 étapes. Une image ou une vidéo YouTube avec une légende par étape. Le guide
        est réservé à l’équipe ; la visibilité de la vidéo se règle dans YouTube.
      </p>
      <div v-for="(step, index) in draft" :key="index">
        <h4>Étape {{ index + 1 }}</h4>
        <div class="offer-tutorial-actions" :aria-label="`Support de l’étape ${index + 1}`">
          <button
            type="button"
            class="button button-secondary"
            :aria-pressed="!('youtubeUrl' in step)"
            @click="selectMedia(index, false)"
          >
            Image
          </button>
          <button
            type="button"
            class="button button-secondary"
            :aria-pressed="'youtubeUrl' in step"
            @click="selectMedia(index, true)"
          >
            Vidéo YouTube
          </button>
        </div>
        <template v-if="'youtubeUrl' in step">
          <label :for="`tutorial-video-${tutorialId ?? 'physical-box'}-${index}`"
            >Lien YouTube de l’étape {{ index + 1 }}</label
          >
          <input
            :id="`tutorial-video-${tutorialId ?? 'physical-box'}-${index}`"
            v-model="step.youtubeUrl"
            type="url"
            placeholder="https://www.youtube.com/watch?v=…"
            maxlength="2048"
            @input="saved = false"
          />
        </template>
        <template v-else>
          <img
            v-if="step.imageDataUrl"
            :src="step.imageDataUrl"
            :alt="`Aperçu étape ${index + 1}`" /><label
            :for="`tutorial-image-${tutorialId ?? 'physical-box'}-${index}`"
            >Image de l’étape {{ index + 1 }}</label
          ><input
            :id="`tutorial-image-${tutorialId ?? 'physical-box'}-${index}`"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            @change="chooseImage(index, $event)" /></template
        ><label :for="`tutorial-caption-${tutorialId ?? 'physical-box'}-${index}`"
          >Légende de l’étape {{ index + 1 }}</label
        ><textarea
          :id="`tutorial-caption-${tutorialId ?? 'physical-box'}-${index}`"
          v-model="step.caption"
          maxlength="500"
          rows="3"
          @input="saved = false"
        />
        <div class="offer-tutorial-actions">
          <button
            type="button"
            class="button button-secondary"
            :disabled="index === 0"
            :aria-label="`Monter l’étape ${index + 1}`"
            @click="move(index, -1)"
          >
            Monter</button
          ><button
            type="button"
            class="button button-secondary"
            :disabled="index === draft.length - 1"
            :aria-label="`Descendre l’étape ${index + 1}`"
            @click="move(index, 1)"
          >
            Descendre</button
          ><button
            type="button"
            class="button button-danger"
            :aria-label="`Retirer l’étape ${index + 1}`"
            @click="
              draft.splice(index, 1);
              saved = false;
            "
          >
            Retirer
          </button>
        </div>
      </div>
      <button
        type="button"
        class="button button-secondary"
        :disabled="draft.length >= 6"
        @click="addStep"
      >
        Ajouter une étape</button
      ><button type="button" class="button button-assign" @click="save">
        {{ busy ? 'Enregistrement…' : 'Enregistrer le tutoriel' }}
      </button>
    </fieldset>
    <p v-if="error" role="alert" class="flow-error">{{ error }}</p>
    <p v-if="saved" role="status">Tutoriel enregistré.</p>
  </div>
</template>

<style scoped>
.tutorial-video {
  display: grid;
  gap: 0.75rem;
  margin-block: 0.75rem;
}
.tutorial-video iframe {
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 200px;
  border: 0;
  border-radius: 0.75rem;
}
.tutorial-content input[type='url'] {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}
.offer-tutorial-actions [aria-pressed='true'] {
  background: #0c2744;
  color: white;
}
</style>
